import { useEffect, useState } from "react";
import api, { formatApiError } from "@/api";
import { useLang } from "@/i18n";
import { ClientLayout } from "./ClientHome";
import { toast } from "sonner";
import { ShoppingBag, ExternalLink, Truck } from "lucide-react";

function ProductCard({ p, paypalEnabled, loading, onBuy }) {
  const { t } = useLang();
  const inStock = p.variants.filter((v) => v.in_stock);
  const [variantId, setVariantId] = useState((inStock[0] || p.variants[0] || {}).sync_variant_id);
  const [qty, setQty] = useState(1);
  const [ship, setShip] = useState(null);
  const variant = p.variants.find((v) => v.sync_variant_id === variantId) || inStock[0];

  useEffect(() => {
    setShip(null);
    if (variant) {
      api.post("/shop/shipping-estimate", { sync_variant_id: variant.sync_variant_id, quantity: qty })
        .then((r) => setShip(r.data)).catch(() => setShip(null));
    }
  }, [variantId, qty]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!variant) return null;
  const total = variant.retail_price * qty + (ship ? ship.shipping_cents / 100 : 0);

  return (
    <div className="kc-card overflow-hidden" data-testid={`shop-product-${p._id}`}>
      {p.thumbnail_url && (
        <img src={p.thumbnail_url} alt={p.name} className="w-full aspect-square object-cover" loading="lazy" />
      )}
      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-black text-lg leading-tight">{p.name}</h2>
          <p className="font-mono font-black text-lg text-primary shrink-0" data-testid={`shop-price-${p._id}`}>
            €{variant.retail_price.toFixed(2)}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("shop.size")}</label>
            <select data-testid={`shop-size-${p._id}`} className="kc-input mt-1 !py-2 text-sm"
              value={variantId || ""} onChange={(e) => setVariantId(Number(e.target.value))}>
              {p.variants.map((v) => (
                <option key={v.sync_variant_id} value={v.sync_variant_id} disabled={!v.in_stock}>
                  {v.size || v.name.split("/").pop().trim()}{v.in_stock ? "" : ` — ${t("shop.outOfStock")}`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("shop.qty")}</label>
            <select data-testid={`shop-qty-${p._id}`} className="kc-input mt-1 !py-2 text-sm"
              value={qty} onChange={(e) => setQty(Number(e.target.value))}>
              {[1, 2, 3].map((q) => <option key={q} value={q}>{q}</option>)}
            </select>
          </div>
        </div>
        <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5" data-testid={`shop-shipping-${p._id}`}>
          <Truck className="w-3.5 h-3.5 text-primary" />
          {ship
            ? <span>+ €{(ship.shipping_cents / 100).toFixed(2)} {t("shop.shipping")} ({ship.delivery} {t("shop.days")})</span>
            : <span>{t("shop.shippingCalc")}</span>}
        </p>
        <button data-testid={`shop-buy-${p._id}`} onClick={() => onBuy(p, variant, qty, "stripe")}
          disabled={!!loading || inStock.length === 0}
          className="kc-btn-primary w-full !py-2.5">
          {loading === `stripe:${p._id}` ? t("common.loading") : `${t("shop.buy")} · €${total.toFixed(2)}`}
        </button>
        {paypalEnabled && (
          <button data-testid={`shop-buy-paypal-${p._id}`} onClick={() => onBuy(p, variant, qty, "paypal")}
            disabled={!!loading || inStock.length === 0}
            className="w-full !py-2.5 rounded-full border-2 border-[#003087] text-[#003087] font-bold text-sm hover:bg-[#003087] hover:text-white transition-colors">
            {loading === `paypal:${p._id}` ? t("common.loading") : t("shop.buyPaypal")}
          </button>
        )}
      </div>
    </div>
  );
}

export default function ClientShop() {
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [paypalEnabled, setPaypalEnabled] = useState(false);
  const [loading, setLoading] = useState("");

  useEffect(() => {
    api.get("/shop/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/client/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/payments/config").then((r) => setPaypalEnabled(!!r.data.paypal_enabled)).catch(() => {});
  }, []);

  const buy = async (p, variant, qty, provider) => {
    setLoading(`${provider}:${p._id}`);
    try {
      const { data } = await api.post("/shop/checkout", {
        sync_variant_id: variant.sync_variant_id, quantity: qty, provider,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url || data.approval_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading("");
    }
  };

  const statusKey = (o) => (o.fulfillment_status && o.fulfillment_status !== "pending")
    ? `shop.status.${o.fulfillment_status}` : `shop.status.${o.payment_status}`;

  return (
    <ClientLayout>
      <div data-testid="client-shop-page" className="max-w-xl mx-auto w-full space-y-6">
        <div>
          <h1 className="text-3xl font-black uppercase tracking-tight flex items-center gap-2">
            <ShoppingBag className="w-7 h-7 text-primary" /> {t("shop.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("shop.sub")}</p>
        </div>

        <div className="space-y-4" data-testid="shop-products">
          {products.length === 0 && (
            <div className="kc-card p-6 text-center text-sm text-muted-foreground" data-testid="shop-empty">{t("shop.empty")}</div>
          )}
          {products.map((p) => (
            <ProductCard key={p._id} p={p} paypalEnabled={paypalEnabled} loading={loading} onBuy={buy} />
          ))}
        </div>

        <section className="space-y-3" data-testid="shop-orders">
          <h2 className="kc-eyebrow">{t("shop.myOrders")}</h2>
          {orders.length === 0 && <p className="text-sm text-muted-foreground">{t("shop.emptyOrders")}</p>}
          {orders.map((o) => (
            <div key={o._id} className="kc-card p-4" data-testid={`shop-order-${o._id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold text-sm truncate">{o.product_name} × {o.quantity}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(o.created_at).toLocaleDateString()} · €{((o.amount_cents + (o.shipping_cents || 0)) / 100).toFixed(2)}
                    {o.shipping_cents ? ` (${t("shop.shippingInclNote")} €${(o.shipping_cents / 100).toFixed(2)})` : ""}
                  </p>
                </div>
                <span className="kc-badge bg-secondary text-muted-foreground shrink-0" data-testid={`shop-order-status-${o._id}`}>
                  {t(statusKey(o))}
                </span>
              </div>
              {o.tracking_url && (
                <a href={o.tracking_url} target="_blank" rel="noreferrer" data-testid={`shop-order-track-${o._id}`}
                  className="text-xs font-bold text-primary hover:underline mt-2 inline-flex items-center gap-1">
                  <ExternalLink className="w-3.5 h-3.5" /> {t("shop.track")}
                </a>
              )}
            </div>
          ))}
        </section>
      </div>
    </ClientLayout>
  );
}
